import React, { useMemo } from 'react';
import type { HotelProposalReasons } from '../../../types/hotelProposalReasons';
import type { HotelSegmentGroup } from '../../../types/proposalBundles';
import { formatRouteDate } from '../../../utils/routeInfoFormatter';
import HotelItemsTable from './HotelItemsTable';
import HotelRecommendationReasonsSection from './HotelRecommendationReasonsSection';

const SEGMENT_COLORS = ['#0095c8', '#2ea872', '#e8913a', '#8b6fc0'] as const;

const segmentColor = (segmentIndex: number): string =>
  SEGMENT_COLORS[segmentIndex % SEGMENT_COLORS.length] ?? SEGMENT_COLORS[0];

const formatSegmentPeriod = (checkin: string | null, checkout: string | null): string | null => {
  const checkinLabel = formatRouteDate(checkin);
  const checkoutLabel = formatRouteDate(checkout);
  if (checkinLabel && checkoutLabel) {
    return `${checkinLabel}〜${checkoutLabel}`;
  }
  return checkinLabel ?? checkoutLabel;
};

interface Props {
  bundleId: number;
  segment: HotelSegmentGroup;
  includeTax: boolean;
  disabled: boolean;
  onSelect: (itemId: number) => void;
  onOpenDetail: (itemId: number) => void;
  isSpLayout?: boolean;
  reasons: HotelProposalReasons;
}

const PartitionedSegmentBlock = ({
  bundleId,
  segment,
  includeTax,
  disabled,
  onSelect,
  onOpenDetail,
  isSpLayout = false,
  reasons
}: Props) => {
  const styles = useMemo(() => getStyles(), []);
  const periodLabel = formatSegmentPeriod(segment.checkin, segment.checkout);
  const selectedInSegment = segment.items.some(item => item.selected);
  const segmentStayDays = segment.items[0]?.hotel.stayDays ?? null;
  const stayLabel =
    segmentStayDays && segmentStayDays > 0 ? `（${segmentStayDays}泊${segmentStayDays + 1}日）` : '';
  const priceHeaderNote = segmentStayDays && segmentStayDays > 0 ? `（${segmentStayDays}泊合計）` : '（1泊1室）';
  const color = segmentColor(segment.index);

  return (
    <div id={`hotel-segment-${bundleId}-${segment.index}`} style={styles.scrollTarget}>
      <div style={styles.segmentBlock}>
        <div style={styles.segmentHeader}>
          <div style={styles.segmentHeaderLeft}>
            <div style={{ ...styles.segmentNumber, backgroundColor: color }}>{segment.index + 1}</div>
            <div>
              <div style={styles.segmentTitle}>
                {periodLabel ?? 'ホテル候補'}
                {stayLabel}
              </div>
            </div>
          </div>
          <span
            style={{
              ...styles.segmentStatus,
              ...(selectedInSegment ? styles.segmentStatusDone : styles.segmentStatusTodo)
            }}
          >
            {selectedInSegment ? '選択済み' : '未選択'}
          </span>
        </div>
        <HotelItemsTable
          items={segment.items}
          includeTax={includeTax}
          bundleStayDays={segmentStayDays}
          priceHeaderNote={priceHeaderNote}
          disabled={disabled}
          onSelect={onSelect}
          onOpenDetail={onOpenDetail}
          isSpLayout={isSpLayout}
        />
        <HotelRecommendationReasonsSection
          items={segment.items}
          reasons={reasons}
          showSummary={segment.index === 0}
          showHeader={segment.index === 0}
          compact
        />
      </div>
    </div>
  );
};

export default PartitionedSegmentBlock;

const getStyles = () => ({
  scrollTarget: {
    scrollMarginTop: '88px'
  },
  segmentBlock: {
    border: '1px solid #ececec',
    borderRadius: '8px',
    marginBottom: '12px',
    overflow: 'hidden' as const
  },
  segmentHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ececec'
  },
  segmentHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  segmentNumber: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: 700 as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  segmentTitle: {
    fontWeight: 700 as const,
    fontSize: '13px'
  },
  segmentStatus: {
    fontSize: '11px',
    fontWeight: 600 as const,
    padding: '3px 10px',
    borderRadius: '12px'
  },
  segmentStatusDone: {
    backgroundColor: '#e8f7ef',
    color: '#2ea872'
  },
  segmentStatusTodo: {
    backgroundColor: '#fff3e6',
    color: '#e8913a'
  }
});
